// Purge Signals Dialog - Admin-only confirmation modal for deleting community signals

import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface SignalStats {
  total: number;
  oldest: string | null;
  newest: string | null;
  byStatus: Record<string, number>;
}

interface PurgeSignalsDialogProps {
  stats: SignalStats;
  onPurgeComplete?: () => void;
}

type PurgeScope = "all" | "status";

export function PurgeSignalsDialog({ stats, onPurgeComplete }: PurgeSignalsDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isPurging, setIsPurging] = useState(false);
  const [scope, setScope] = useState<PurgeScope>("all");
  const [statusFilter, setStatusFilter] = useState<string>("NEW");

  const isConfirmValid = confirmText === "DELETE";

  const getImpactCount = () => {
    if (scope === "all") return stats.total;
    if (scope === "status") return stats.byStatus[statusFilter] || 0;
    return 0;
  };

  const handlePurge = async () => {
    if (!isConfirmValid) return;

    setIsPurging(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Authentication required");
      }

      const body: Record<string, unknown> = { scope };
      if (scope === "status") {
        body.status = statusFilter;
      }

      const response = await supabase.functions.invoke("purge-community-signals", {
        body,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const result = response.data;

      if (result.success) {
        toast({
          title: "Signals purged successfully",
          description: `Deleted ${result.deleted_count} signals${result.reset_state.cursor_reset ? ". Ingestion state reset." : ""}`
        });
        
        // Invalidate queries to refresh data
        queryClient.invalidateQueries({ queryKey: ["community-signals"] });
        queryClient.invalidateQueries({ queryKey: ["community-ingestion-settings"] });
        
        onPurgeComplete?.();
        setOpen(false);
        setConfirmText("");
      } else {
        throw new Error(result.error_message || "Purge failed");
      }
    } catch (error) {
      toast({
        title: "Purge failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsPurging(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" className="gap-2">
          <Trash2 className="h-4 w-4" />
          Delete All Signals
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Ingested Signals
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <div className="bg-muted p-3 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>Total signals:</span>
                <Badge variant="secondary">{stats.total}</Badge>
              </div>
              <div className="flex justify-between text-sm">
                <span>Oldest:</span>
                <span className="text-xs text-muted-foreground">{formatDate(stats.oldest)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Newest:</span>
                <span className="text-xs text-muted-foreground">{formatDate(stats.newest)}</span>
              </div>
              <div className="flex gap-1 flex-wrap pt-1">
                {Object.entries(stats.byStatus).map(([status, count]) => (
                  <Badge key={status} variant="outline" className="text-xs">
                    {status}: {count}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Scope Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Delete scope</Label>
              <Select value={scope} onValueChange={(v) => setScope(v as PurgeScope)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All signals (full reset)</SelectItem>
                  <SelectItem value="status">By status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scope === "status" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-foreground">Status to delete</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEW">NEW ({stats.byStatus.NEW || 0})</SelectItem>
                    <SelectItem value="REVIEWED">REVIEWED ({stats.byStatus.REVIEWED || 0})</SelectItem>
                    <SelectItem value="DISMISSED">DISMISSED ({stats.byStatus.DISMISSED || 0})</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Impact Warning */}
            <div className="bg-destructive/10 border border-destructive/20 p-3 rounded-lg">
              <p className="text-sm text-destructive font-medium">
                This will delete {getImpactCount()} signal{getImpactCount() !== 1 ? "s" : ""}.
                {scope === "all" && " Ingestion cursors will be reset."}
              </p>
            </div>

            {/* Confirmation Input */}
            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-sm font-medium text-foreground">
                Type <span className="font-mono bg-muted px-1 rounded">DELETE</span> to confirm
              </Label>
              <Input
                id="confirm-delete"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="font-mono"
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setConfirmText("")}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handlePurge}
            disabled={!isConfirmValid || isPurging}
          >
            {isPurging ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete {getImpactCount()} Signals
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
